import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Avatar, Box, Chip, Divider, InputAdornment, Stack, TextField, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import GoBackButton from 'components/shared/buttons/GoBackButton';
import LoadingBlock from 'components/shared/blocks/LoadingBlock';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import StyledNewsBodyContainer from 'common/theme/news-view/StyledNewsBodyContainer';
import ContentRenderer from 'components/shared/content/ContentRenderer';
import { isAdmin } from 'common/utils/user';
import requestStatus from 'common/constants/status';
import { buildInitialValues, extractTokens, fillBody, tokenLabel } from 'common/constants/legalTokens';
import AppBarContext from 'context/AppBarContext';
import legalSlice from 'store/modules/legal';

function LegalView() {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation(['legal', 'routes', 'buttons']);
  const pageTitle = t('legal.list', { ns: 'routes' });
  const { id } = useParams();
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const legal = useSelector((state) => state.legal.data.currentLegal);
  const acceptance = useSelector((state) => state.legal.data.acceptance);
  const acceptances = useSelector((state) => state.legal.data.acceptances);
  const acceptStatus = useSelector((state) => state.legal.acceptStatus);
  const role = useSelector((state) => state.auth.user.role);
  const user = useSelector((state) => state.auth.user);
  const status = useSelector((state) => state.legal.status);
  const uiLanguage = useSelector((state) => state.settings.config.language);

  const isAdminUser = isAdmin(role);

  // Admin ve la plantilla con los tokens literales ({{nombre}}); solo el cliente los rellena.
  const tokens = useMemo(() => (isAdminUser ? [] : extractTokens(legal?.body)), [isAdminUser, legal]);
  const hasTokens = tokens.length > 0;
  const isAccepted = Boolean(acceptance?.accepted_at);

  const [values, setValues] = useState({});
  const [acceptSearch, setAcceptSearch] = useState('');

  useEffect(() => {
    if (role && isAdminUser) {
      dispatch(legalSlice.actions.find(id));
      dispatch(legalSlice.actions.getAcceptances(id));
    } else {
      dispatch(legalSlice.actions.getLatestByRole({ language: uiLanguage, role }));
    }
  }, [dispatch, id, isAdminUser, role, uiLanguage]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle('');
  }, [setAppBarButton, setAppBarTitle]);

  // Cliente con documento cargado: traer su aceptación previa para releerlo con sus datos.
  useEffect(() => {
    if (!isAdminUser && legal?.id) {
      dispatch(legalSlice.actions.getMyAcceptance(legal.id));
    }
    return () => { dispatch(legalSlice.actions.clearAcceptance()); };
  }, [dispatch, isAdminUser, legal?.id]);

  // Autollenar desde el perfil cuando el documento tiene campos y aún no se ha aceptado.
  useEffect(() => {
    if (hasTokens && !isAccepted) {
      setValues(buildInitialValues(tokens, user, i18n.language));
    }
  }, [hasTokens, isAccepted, tokens, user, i18n.language]);

  // Documento mostrado: con los valores aceptados (read-only), con el formulario en vivo, o tal cual.
  const renderedBody = useMemo(() => {
    if (!legal?.body) return legal?.body;
    if (isAccepted) return fillBody(legal.body, acceptance.values || {});
    if (hasTokens) return fillBody(legal.body, values);
    return legal.body;
  }, [legal, isAccepted, acceptance, hasTokens, values]);

  const allFilled = tokens.every((key) => String(values[key] ?? '').trim() !== '');
  const onAccept = () => dispatch(legalSlice.actions.accept({ legalId: legal.id, values }));

  const acceptedCount = useMemo(() => acceptances.filter((a) => a.accepted).length, [acceptances]);
  const filteredAcceptances = useMemo(() => {
    const q = acceptSearch.trim().toLowerCase();
    if (!q) return acceptances;
    return acceptances.filter((a) => (a.user?.full_name || '').toLowerCase().includes(q));
  }, [acceptances, acceptSearch]);

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      {isAdminUser && <GoBackButton />}
      <LoadingBlock
        emptyBlockProps={{ message: t('empty', { ns: 'legal' }) }}
        isValid={legal !== null}
        status={status}
        type="legal">
        {legal && (
          // Misma columna de lectura editorial que las noticias (760px centrada).
          <Box component="article" sx={{ maxWidth: 760, mx: 'auto', width: '100%' }}>
            <Typography
              component="h1"
              align="center"
              sx={{
                color: 'text.primary',
                fontWeight: 800,
                letterSpacing: '-0.02em',
                lineHeight: 1.15,
                fontSize: { xs: '1.7rem', sm: '2rem', md: '2.3rem' },
                mb: 2,
              }}>
              {legal.title}
            </Typography>

            {isAccepted && (
              <Stack direction="row" justifyContent="center" sx={{ mb: 2 }}>
                <Chip
                  size="small"
                  variant="outlined"
                  color="success"
                  label={t('acceptedOn', {
                    ns: 'legal',
                    date: new Date(acceptance.accepted_at).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'es-CO'),
                    defaultValue: `Aceptado el ${new Date(acceptance.accepted_at).toLocaleDateString('es-CO')}`,
                  })}
                />
              </Stack>
            )}

            <Divider sx={{ borderColor: 'hairline.main', mb: 1 }} />

            {hasTokens && !isAccepted && (
              <Box sx={{ backgroundColor: 'surface.content', border: '1px solid', borderColor: 'hairline.main', borderRadius: '16px', p: { xs: 2, md: 3 }, my: 3, boxShadow: (th) => th.palette.shadow.card }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
                  {t('fillYourData', { ns: 'legal', defaultValue: 'Completa tus datos' })}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  {t('fillYourDataHint', { ns: 'legal', defaultValue: 'Revisa y completa los campos; se insertarán en el documento de abajo.' })}
                </Typography>
                <Grid container spacing={2}>
                  {tokens.map((key) => (
                    <Grid xs={12} sm={6} key={key}>
                      <TextField
                        label={tokenLabel(key, i18n.language)}
                        value={values[key] || ''}
                        onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            <StyledNewsBodyContainer>
              <ContentRenderer value={renderedBody} />
            </StyledNewsBodyContainer>

            {/* Admin: quiénes aceptaron este documento (nombre, estado, fecha) + buscador. */}
            {isAdminUser && (
              <Box sx={{ mt: 4, backgroundColor: 'surface.content', border: '1px solid', borderColor: 'hairline.main', borderRadius: '16px', p: { xs: 2, md: 3 } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
                  <Typography sx={{ fontWeight: 800 }}>
                    {t('acceptancesTitle', { ns: 'legal', defaultValue: 'Aceptaciones' })}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Chip size="small" color="success" variant="outlined" label={t('acceptedCountLabel', { ns: 'legal', count: acceptedCount, defaultValue: `${acceptedCount} aceptaron` })} />
                    <Chip size="small" variant="outlined" label={t('pendingCountLabel', { ns: 'legal', count: acceptances.length - acceptedCount, defaultValue: `${acceptances.length - acceptedCount} pendientes` })} sx={{ color: 'text.secondary', borderColor: 'hairline.strong' }} />
                  </Stack>
                </Stack>
                <TextField
                  fullWidth
                  size="small"
                  value={acceptSearch}
                  onChange={(e) => setAcceptSearch(e.target.value)}
                  sx={{ mb: 2 }}
                  placeholder={t('searchUser', { ns: 'legal', defaultValue: 'Buscar usuario…' })}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment> }}
                />
                {filteredAcceptances.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                    {acceptances.length === 0
                      ? t('noAcceptances', { ns: 'legal', defaultValue: 'Aún no hay usuarios para este documento.' })
                      : t('noResults', { ns: 'legal', defaultValue: 'Sin resultados.' })}
                  </Typography>
                ) : (
                  <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
                    {filteredAcceptances.map((a, idx) => (
                      <Box key={a.user.id}>
                        {idx > 0 && <Divider sx={{ borderColor: 'hairline.main' }} />}
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ py: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                            <Avatar src={a.user.photo} alt={a.user.full_name} sx={{ width: 32, height: 32, fontSize: '0.8rem' }}>{a.user.full_name?.[0]}</Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{a.user.full_name}</Typography>
                          </Stack>
                          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0 }}>
                            {a.accepted_at && (
                              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                {new Date(a.accepted_at).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'es-CO')}
                              </Typography>
                            )}
                            <Chip
                              size="small"
                              variant={a.accepted ? 'filled' : 'outlined'}
                              color={a.accepted ? 'success' : 'default'}
                              label={a.accepted ? t('acceptedChip', { ns: 'legal', defaultValue: 'Aceptó' }) : t('pendingChip', { ns: 'legal', defaultValue: 'Pendiente' })}
                              sx={a.accepted ? undefined : { color: 'text.secondary', borderColor: 'hairline.strong' }}
                            />
                          </Stack>
                        </Stack>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* El cliente acepta SIEMPRE (con o sin campos); admin solo ve la plantilla. */}
            {!isAdminUser && !isAccepted && (
              <Stack alignItems="center" sx={{ mt: 4 }}>
                <PrimaryLoadingButton
                  label={t('acceptAndConfirm', { ns: 'legal', defaultValue: 'Acepto y confirmo' })}
                  loading={acceptStatus === requestStatus.loading}
                  disabled={!allFilled || acceptStatus === requestStatus.loading}
                  onClick={onAccept}
                />
                {!allFilled && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1 }}>
                    {t('fillAllToAccept', { ns: 'legal', defaultValue: 'Completa todos los campos para poder aceptar.' })}
                  </Typography>
                )}
              </Stack>
            )}
          </Box>
        )}
      </LoadingBlock>
    </>
  );
}

export default LegalView;
